/**
 * C2 tab (`ldt-c2`, spec §10):
 *   - Link Availability & Continuity card with time-window pills + step chart
 *   - Control Sticks / Dock card (swaps mode based on `flightOps.deploymentType` — mocked as 'traditional')
 *   - Per-link cards (1/2/3) with status / latency / rssi / tx-rx bytes / continuity %
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { DroneLive } from '@/lib/store/drones';
import type { C2Link } from '@/lib/mqtt/schemas/c2links';
import { Section, SectionGroup } from '@/components/Section';
import { Dot } from '@/components/ui/dot';
import { Badge } from '@/components/ui/badge';
import { linkClass } from '@/lib/store/drones';
import { cn } from '@/lib/utils';

const WINDOWS = [1, 5, 15, 60] as const;
type Win = (typeof WINDOWS)[number];

function continuityTone(pct: number | null | undefined) {
  if (pct == null) return 'muted';
  if (pct >= 99) return 'ok';
  if (pct >= 95) return 'warn';
  return 'crit';
}
function latencyQuality(ms: number | null | undefined): { label: string; tone: 'ok' | 'warn' | 'crit' | 'muted' } {
  if (ms == null) return { label: '—', tone: 'muted' };
  if (ms < 80) return { label: 'Excellent', tone: 'ok' };
  if (ms < 150) return { label: 'Good', tone: 'ok' };
  if (ms < 250) return { label: 'Marginal', tone: 'warn' };
  return { label: 'Poor', tone: 'crit' };
}
function rssiQuality(dBm: number | null | undefined): { label: string; tone: 'ok' | 'warn' | 'crit' | 'muted' } {
  if (dBm == null) return { label: '—', tone: 'muted' };
  if (dBm >= -70) return { label: 'Excellent', tone: 'ok' };
  if (dBm >= -85) return { label: 'Good', tone: 'ok' };
  if (dBm >= -95) return { label: 'Marginal', tone: 'warn' };
  return { label: 'Poor', tone: 'crit' };
}

export function C2Tab({ drone }: { drone: DroneLive }) {
  const c2 = drone.c2;
  const [win, setWin] = useState<Win>(1);

  const linksUp = (c2?.links ?? []).filter((l) => l.status === 'online').length;
  const linksTotal = c2?.links.length ?? 0;
  const availTone: 'ok' | 'warn' | 'crit' = linksUp === linksTotal && linksTotal > 0 ? 'ok' : linksUp === 0 ? 'crit' : 'warn';
  const cTone = continuityTone(c2?.continuity_pct);

  return (
    <SectionGroup>
      {/* Availability card */}
      <article className="rounded-md border border-(--color-border) bg-(--color-bg) p-3 space-y-2">
        <header className="flex items-center justify-between">
          <h3 className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
            Link Availability &amp; Continuity
          </h3>
          <div className="flex gap-0.5 rounded-full bg-(--color-surface) border border-(--color-border) p-0.5">
            {WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWin(w)}
                className={cn(
                  'h-5 px-2 rounded-full text-[0.615em] font-semibold transition-colors',
                  win === w ? 'bg-(--color-selected) text-(--color-selected-fg)' : 'text-(--color-text3) hover:text-(--color-text2)',
                )}
              >
                {w}m
              </button>
            ))}
          </div>
        </header>
        <div className="flex items-center gap-3">
          <div className="flex items-baseline gap-1.5">
            <span
              className={cn(
                'text-base font-bold',
                availTone === 'ok' && 'text-(--color-green)',
                availTone === 'warn' && 'text-(--color-amber)',
                availTone === 'crit' && 'text-(--color-red)',
              )}
            >
              {linksUp} / {linksTotal} links
            </span>
            <Badge variant={availTone}>{availTone === 'ok' ? 'All Links Active' : availTone === 'warn' ? 'Degraded' : 'No Links'}</Badge>
          </div>
          <div className="h-3 w-px bg-(--color-border)" />
          <div className="flex items-baseline gap-1">
            <span
              className={cn(
                'text-base font-bold',
                cTone === 'ok' && 'text-(--color-green)',
                cTone === 'warn' && 'text-(--color-amber)',
                cTone === 'crit' && 'text-(--color-red)',
              )}
            >
              {c2?.continuity_pct ?? '—'}%
            </span>
            <span className="text-[0.692em] text-(--color-text3)">available</span>
            <span className="text-[0.692em] font-mono text-(--color-text3)">· {win}m</span>
          </div>
        </div>
        <AvailabilityStepChart linksUp={linksUp} linksTotal={linksTotal} windowMin={win} />
      </article>

      {/* Control Sticks / Dock card — branches on c2.deployment (mockup
         `renderC2Tab` swaps these two card shapes based on the drone's
         deployment type). */}
      {c2?.deployment === 'dock' ? (
        <Section title="Dock">
          <article className="rounded-md border border-(--color-border) border-t-2 border-t-(--color-blue) bg-(--color-surface) p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[0.846em] font-bold">Drone Dock</div>
                <div className="text-[0.692em] text-(--color-text3) font-mono">{c2.dock_id ?? '—'}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <Dot variant="info" size="sm" />
                <span className="text-[0.769em] font-bold text-(--color-blue)">Deployed</span>
              </div>
            </div>
          </article>
        </Section>
      ) : (
        <Section title="Control Sticks">
          <article className="rounded-md border border-(--color-border) border-t-2 border-t-(--color-green) bg-(--color-surface) p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[0.846em] font-bold">Control Sticks</div>
                <div className="text-[0.692em] text-(--color-text3) font-mono">{c2?.sticks_type ?? '—'}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <Dot variant={c2?.sticks_type ? 'ok' : 'muted'} size="sm" />
                <span
                  className={cn(
                    'text-[0.769em] font-bold',
                    c2?.sticks_type ? 'text-(--color-green)' : 'text-(--color-text3)',
                  )}
                >
                  {c2?.sticks_type ? 'Connected' : '—'}
                </span>
              </div>
            </div>
          </article>
        </Section>
      )}

      {/* Per-link cards */}
      <Section title="Links">
        <div className="space-y-2">
          {(c2?.links ?? []).map((l, idx) => (
            <LinkCard key={l.label + idx} link={l} index={idx} />
          ))}
          {linksTotal === 0 && <div className="text-[0.769em] text-(--color-text3)">No C2 links reported yet.</div>}
        </div>
      </Section>
    </SectionGroup>
  );
}

/* ---------- private ---------- */

function LinkCard({ link, index }: { link: C2Link; index: number }) {
  const tone = linkClass(link.status);
  const lat = latencyQuality(link.latency_ms);
  const rssi = rssiQuality(link.rssi);
  const pos = ['Primary', 'Secondary', 'Tertiary'][index] ?? `Link ${index + 1}`;

  return (
    <article
      className={cn(
        'rounded-md border border-(--color-border) bg-(--color-surface) p-3 border-t-2 space-y-2',
        tone === 'ok' && 'border-t-(--color-green)',
        tone === 'warn' && 'border-t-(--color-amber)',
        tone === 'crit' && 'border-t-(--color-red)',
      )}
    >
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[0.692em] font-bold uppercase tracking-wider text-(--color-text3)">{pos}</span>
          <span className="text-[0.692em] font-mono text-(--color-text2)">{link.tech}</span>
          {link.is_lead && <Badge variant="info">LEAD</Badge>}
        </div>
        <div className="flex items-center gap-1.5">
          <Dot variant={tone} size="xs" />
          <span
            className={cn(
              'text-[0.769em] font-bold',
              tone === 'ok' && 'text-(--color-green)',
              tone === 'warn' && 'text-(--color-amber)',
              tone === 'crit' && 'text-(--color-red)',
            )}
          >
            {link.status === 'online' ? 'Online' : link.status === 'degraded' ? 'Degraded' : 'Offline'}
          </span>
        </div>
      </header>
      <div className="grid grid-cols-2 gap-1.5 text-[0.692em]">
        <Field label="Latency" value={link.latency_ms != null ? `${link.latency_ms} ms` : '—'} qual={lat.label} qualTone={lat.tone} />
        <Field label="RSSI" value={link.rssi != null ? `${link.rssi} dBm` : '—'} qual={rssi.label} qualTone={rssi.tone} />
        <Field label="TX" value={`${(link.tx_bytes ?? 0).toLocaleString()} B`} />
        <Field label="RX" value={`${(link.rx_bytes ?? 0).toLocaleString()} B`} />
      </div>
      {link.status === 'offline' && (
        <div className="text-[0.692em] text-(--color-red) font-semibold">⚠ Link lost</div>
      )}
    </article>
  );
}

function Field({
  label,
  value,
  qual,
  qualTone,
}: {
  label: string;
  value: string;
  qual?: string;
  qualTone?: 'ok' | 'warn' | 'crit' | 'muted';
}) {
  return (
    <div className="rounded bg-(--color-bg) px-2 py-1 flex items-center justify-between">
      <span className="text-(--color-text3) font-mono">{label}</span>
      <span className="font-mono font-semibold flex items-center gap-1.5">
        {value}
        {qual && qualTone && (
          <span
            className={cn(
              'text-[0.615em] font-bold uppercase',
              qualTone === 'ok' && 'text-(--color-green)',
              qualTone === 'warn' && 'text-(--color-amber)',
              qualTone === 'crit' && 'text-(--color-red)',
              qualTone === 'muted' && 'text-(--color-text3)',
            )}
          >
            {qual}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * Tiny step chart of links-up over time. Uses an inline ring buffer.
 * For a real impl, swap to uPlot subscribed to a `C2_HISTORY[droneId]` time-series.
 */
function AvailabilityStepChart({ linksUp, linksTotal, windowMin }: { linksUp: number; linksTotal: number; windowMin: number }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bufRef = useRef<number[]>([]);

  useEffect(() => {
    bufRef.current.push(linksUp);
    const cap = windowMin * 60; // 1 sample/sec
    if (bufRef.current.length > cap) bufRef.current.splice(0, bufRef.current.length - cap);
    draw();
  }, [linksUp, windowMin]);

  const draw = useMemo(
    () => () => {
      const c = canvasRef.current;
      if (!c) return;
      const w = c.clientWidth;
      const h = 64;
      c.width = w * devicePixelRatio;
      c.height = h * devicePixelRatio;
      const ctx = c.getContext('2d')!;
      ctx.scale(devicePixelRatio, devicePixelRatio);
      ctx.clearRect(0, 0, w, h);
      const data = bufRef.current;
      if (data.length < 2) return;
      const max = Math.max(linksTotal, 1);
      const stepX = w / (windowMin * 60);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#22a050';
      ctx.beginPath();
      let started = false;
      for (let i = 0; i < data.length; i++) {
        const v = data[i]!;
        const x = (windowMin * 60 - (data.length - i)) * stepX;
        const y = h - (v / max) * (h - 6) - 3;
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    },
    [linksTotal, windowMin],
  );

  return (
    <div className="relative h-16">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      <div className="absolute bottom-0 right-0 text-[0.538em] font-mono text-(--color-text3)">{windowMin}m → now</div>
    </div>
  );
}
